import { Alert, Button, Stack } from "@assetwatch/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CmmsIntegrationAccordion } from "../components/CmmsIntegrationAccordion";
import { ActiveSubscriptionsSection } from "../components/marketplace/ActiveSubscriptionsSection";
import { AllSubscriptionsModal } from "../components/marketplace/AllSubscriptionsModal";
import { AvailableCapabilitiesSection } from "../components/marketplace/AvailableCapabilitiesSection";
import { CapabilityRequestModal } from "../components/marketplace/CapabilityRequestModal";
import { CustomerSelect } from "../components/marketplace/CustomerSelect";
import { MarketplaceLoadingState } from "../components/marketplace/MarketplaceLoadingState";
import {
  useMarketplace,
  useMarketplaceCustomers,
} from "../queries/useMarketplace";
import type { SelectedMarketplaceRequest } from "../types/marketplace";

type HomeScreenProps = {
  externalCustomerId?: string;
  onCustomerChange: (externalCustomerId: string | undefined) => void;
};

function LoadErrorAlert({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();

  return (
    <Alert
      tone="negative"
      title={t("marketplace.loadErrorTitle")}
      subtext={t("marketplace.loadErrorDescription")}
      action={
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t("common.tryAgain")}
        </Button>
      }
    />
  );
}

export function HomeScreen({
  externalCustomerId,
  onCustomerChange,
}: HomeScreenProps) {
  const { t } = useTranslation();
  const customersQuery = useMarketplaceCustomers();
  const customers = customersQuery.data?.customers ?? [];
  // A customer in the URL that the user cannot access is never requested; the
  // user picks one of their own customers instead of hitting a 403.
  const requestedCustomerIsAccessible =
    externalCustomerId === undefined ||
    customers.some(
      (customer) => customer.externalCustomerId === externalCustomerId,
    );
  const marketplaceQuery = useMarketplace(
    externalCustomerId,
    customersQuery.isSuccess &&
      (externalCustomerId
        ? requestedCustomerIsAccessible
        : customers.length === 1),
  );
  const marketplace = marketplaceQuery.data;
  const [selectedRequest, setSelectedRequest] =
    useState<SelectedMarketplaceRequest | null>(null);
  const [showAllSubscriptions, setShowAllSubscriptions] = useState(false);

  if (customersQuery.isLoading && !marketplace) {
    return <MarketplaceLoadingState />;
  }

  if (customersQuery.isError) {
    return <LoadErrorAlert onRetry={() => void customersQuery.refetch()} />;
  }

  if (customersQuery.isFetched && customers.length === 0) {
    return (
      <Alert
        tone="informative"
        title={t("marketplace.noCustomerAccessTitle")}
        subtext={t("marketplace.noCustomerAccessDescription")}
      />
    );
  }

  if (
    !requestedCustomerIsAccessible ||
    (customers.length > 1 && !externalCustomerId)
  ) {
    return (
      <Stack gap={16} fullWidth>
        <Alert
          tone="informative"
          title={
            requestedCustomerIsAccessible
              ? t("marketplace.selectCustomerTitle")
              : t("marketplace.customerUnavailableTitle")
          }
          subtext={
            requestedCustomerIsAccessible
              ? t("marketplace.selectCustomerDescription")
              : t("marketplace.customerUnavailableDescription")
          }
        />
        <CustomerSelect customers={customers} onChange={onCustomerChange} />
      </Stack>
    );
  }

  if (marketplaceQuery.isLoading) return <MarketplaceLoadingState />;

  if (marketplaceQuery.isError || !marketplace) {
    return <LoadErrorAlert onRetry={() => void marketplaceQuery.refetch()} />;
  }

  const hasContent =
    marketplace.activeSubscriptions.length > 0 ||
    marketplace.availableCapabilities.length > 0;

  return (
    <Stack gap={32} fullWidth>
      {customers.length > 1 ? (
        <CustomerSelect
          customers={customers}
          value={marketplace.customer.externalCustomerId}
          onChange={onCustomerChange}
        />
      ) : null}

      {!hasContent ? (
        <Alert
          tone="informative"
          title={t("marketplace.noCapabilitiesTitle")}
          subtext={t("marketplace.noCapabilitiesDescription")}
        />
      ) : null}

      <ActiveSubscriptionsSection
        marketplace={marketplace}
        onViewAll={() => setShowAllSubscriptions(true)}
        onSelect={setSelectedRequest}
      />

      <AvailableCapabilitiesSection
        capabilities={marketplace.availableCapabilities}
        onSelect={setSelectedRequest}
      />

      <CmmsIntegrationAccordion />

      <AllSubscriptionsModal
        marketplace={marketplace}
        open={showAllSubscriptions}
        onOpenChange={setShowAllSubscriptions}
        onSelect={setSelectedRequest}
      />

      <CapabilityRequestModal
        externalCustomerId={marketplace.customer.externalCustomerId}
        request={selectedRequest}
        onClose={() => setSelectedRequest(null)}
      />
    </Stack>
  );
}
